import { ServiceBikePartAfterService } from "./service-bike"
import { ServiceAction } from "./service-part-action"

const SERVICE_ACTIONS: ServiceAction[] = [
  ServiceAction.BASIC_SERVICE,
  ServiceAction.FULL_SERVICE,
]

const isServicePart = (part: ServiceBikePartAfterService): boolean =>
  SERVICE_ACTIONS.includes(part.recommendedAction) ||
  SERVICE_ACTIONS.includes(part.actionThatWillBePerformed)

/**
 * Lower sorts first. Parts the customer declined lead, since they are the ones
 * a mechanic or customer needs to react to, and parts that needed nothing at
 * all trail, since they are purely informational. A mechanic comment always
 * pulls a part ahead of its comment-less peers.
 */
const getPriority = (part: ServiceBikePartAfterService): number => {
  const actionWasRecommended =
    part.recommendedAction !== ServiceAction.DO_NOTHING
  const actionWillBePerformed =
    part.actionThatWillBePerformed !== ServiceAction.DO_NOTHING
  const hasComment = !!part.commentFromMechanic

  if (!actionWasRecommended && !actionWillBePerformed) {
    return hasComment ? 4 : 5
  }
  if (!actionWillBePerformed) {
    return hasComment ? 0 : 1
  }
  return hasComment ? 2 : 3
}

/**
 * The one canonical order for the "Cykelns skick" list, so the CRM and the
 * marketplace tell the same story: grundservice/fullservice first, then
 * declined actions, then performed actions, then parts that needed nothing.
 * Sorting happens in the API — both clients render the list as they get it.
 */
export const sortBikeConditionParts = (
  parts: ServiceBikePartAfterService[],
): ServiceBikePartAfterService[] =>
  [...parts].sort((a, b) => {
    const aIsService = isServicePart(a)
    const bIsService = isServicePart(b)
    if (aIsService !== bIsService) return aIsService ? -1 : 1

    const priorityDiff = getPriority(a) - getPriority(b)
    if (priorityDiff !== 0) return priorityDiff

    // Prisma row order is not stable enough to leave the rest to chance now
    // that the no-action group can be the majority of the list.
    return a.partName.localeCompare(b.partName, "sv")
  })
